/**
 * Quem chama o *worker* da análise espacial (onda 316, R7): um *module worker* por página, no molde do
 * `curvas.worker` do mapa — `new Worker(new URL('./espacial.worker.ts', import.meta.url), { type: 'module',
 * name: 'espacial-cptu' })`, sem `blob:` (a CSP do Portal tem `worker-src 'self'`). Duas contas, a seção 2D
 * (`PedidoDaSecao`) e a grade do 3D (`PedidoDaGrade`, Tarefa 316.3), na MESMA fila: o último pedido vence, e o
 * anterior ainda pendente — seção ou grade — é cancelado (a promessa rejeita com `CANCELADO`). Sem worker — o
 * navegador não o cria, ou ele quebra —, a conta roda na página, e a resposta diz `naPagina: true` para a tela
 * escrever «calculado na página».
 *
 * Não é puro (fala com `Worker` e `performance`): fica fora do empacote dos puros do `300`/`303`, mas SAI pelo
 * `index.ts` — importá-lo não cria nada (o worker só nasce no primeiro pedido). Quem fica fora do `index.ts` é o
 * `espacial.worker.ts` (M6 da revisão da 316.2).
 * A pasta é a MESMA em `web/` e `portal-web/` (spec §3.2).
 */
import { calcularGrade, calcularSecao } from './espacialDaCampanha';
import type { PedidoDaGrade, PedidoDaSecao, RespostaDaGrade, RespostaDaSecao } from './espacialDaCampanha';

/** Onde a conta rodou e quanto levou. */
type ComTempo = { naPagina: boolean; ms: number };

/** A resposta da seção, com onde ela rodou e quanto levou. */
export type RespostaDoTrabalhador = RespostaDaSecao & ComTempo;

/** A resposta da grade do 3D, com onde ela rodou e quanto levou. */
export type RespostaDaGradeDoTrabalhador = RespostaDaGrade & ComTempo;

/** O erro com que a promessa de um pedido cancelado rejeita. */
export const CANCELADO = 'cancelado: outro pedido veio depois';

/** Um pedido de qualquer das duas contas. */
type Pedido = PedidoDaSecao | PedidoDaGrade;
type Resultado = RespostaDaSecao | RespostaDaGrade;

/** A mensagem que volta do worker (o tipo mora aqui para quem chama não importar o arquivo do worker). */
type Resposta = { id: number; resposta: Resultado; ms: number } | { id: number; erro: string; ms: number };

/** O pedido é o da grade (tem o passo em três eixos; o da seção tem a linha). */
const eDaGrade = (p: Pedido): p is PedidoDaGrade => 'passo' in p;

let trabalhador: Worker | null = null;
let quebrado = false;
let proximoId = 1;

/** Um pedido à espera, no worker ou na página; `naPagina` = a conta já foi para o `setTimeout` da página. */
interface Pendente { ok(r: Resultado & ComTempo): void; falha(e: Error): void; inicio: number; pedido: Pedido; naPagina: boolean }
const pendentes = new Map<number, Pendente>();

/** A conta na página, com o tempo desde o pedido. */
function naPagina(inicio: number, pedido: Pedido): Resultado & ComTempo {
  const r = eDaGrade(pedido) ? calcularGrade(pedido) : calcularSecao(pedido);
  return { ...r, naPagina: true, ms: performance.now() - inicio };
}

/** Roda na página, num tique à parte (a tela desenha «Calculando…» antes de a conta prender a página). */
function agendarNaPagina(id: number): void {
  const p = pendentes.get(id);
  if (!p) return;
  p.naPagina = true;
  setTimeout(() => {
    // Cancelado no meio do caminho: a promessa já rejeitou.
    if (pendentes.get(id) !== p) return;
    pendentes.delete(id);
    try { p.ok(naPagina(p.inicio, p.pedido)); } catch (e) { p.falha(e instanceof Error ? e : new Error(String(e))); }
  }, 0);
}

/** O *worker* da página, criado uma vez; null quando o navegador não o cria (ou ele quebrou). */
function oTrabalhador(): Worker | null {
  if (trabalhador) return trabalhador;
  if (quebrado || typeof Worker === 'undefined') return null;
  let w: Worker;
  try {
    // O Vite reconhece esta forma (a URL dentro do `new Worker`) e empacota o worker como arquivo `.js` do site.
    w = new Worker(new URL('./espacial.worker.ts', import.meta.url), { type: 'module', name: 'espacial-cptu' });
  } catch {
    quebrado = true;
    return null;
  }
  w.onmessage = (e: MessageEvent<Resposta>) => {
    const r = e.data;
    const p = pendentes.get(r.id);
    if (!p || p.naPagina) return; // cancelado no meio do caminho (ou já refeito na página)
    pendentes.delete(r.id);
    if ('resposta' in r) p.ok({ ...r.resposta, naPagina: false, ms: r.ms });
    else p.falha(new Error(`análise espacial: ${r.erro}`));
  };
  const desistir = () => {
    // O worker não carregou (a CSP, um arquivo que não veio), ou a resposta não desserializa: daqui em diante a conta
    // roda na página, e o que esperava por ele também — nenhuma promessa fica pendurada (M7 da revisão da 316.2).
    quebrado = true;
    trabalhador = null;
    try { w.terminate(); } catch { /* nada a desfazer */ }
    for (const [id, p] of pendentes) if (!p.naPagina) agendarNaPagina(id);
  };
  w.onerror = desistir;
  w.onmessageerror = desistir;
  trabalhador = w;
  return w;
}

/**
 * Calcula a seção 2D ou a grade do 3D no worker (ou na página, sem ele). O último pedido vence: chamar de novo —
 * com qualquer dos dois pedidos — cancela o anterior ainda pendente, cuja promessa rejeita com `CANCELADO`;
 * `cancelar()` faz o mesmo com este.
 */
export function calcularNoTrabalhador(pedido: PedidoDaSecao): { resposta: Promise<RespostaDoTrabalhador>; cancelar(): void };
export function calcularNoTrabalhador(pedido: PedidoDaGrade): { resposta: Promise<RespostaDaGradeDoTrabalhador>; cancelar(): void };
export function calcularNoTrabalhador(pedido: Pedido): { resposta: Promise<Resultado & ComTempo>; cancelar(): void } {
  // O último vence: o que ainda espera é cancelado.
  for (const [id, p] of pendentes) {
    pendentes.delete(id);
    p.falha(new Error(CANCELADO));
  }
  const id = proximoId++;
  const inicio = performance.now();
  const resposta = new Promise<Resultado & ComTempo>((ok, falha) => {
    pendentes.set(id, { ok, falha, inicio, pedido, naPagina: false });
  });
  const w = oTrabalhador();
  if (!w) agendarNaPagina(id);
  else {
    try {
      w.postMessage({ id, pedido });
    } catch {
      // O pedido não serializa (não deveria): a conta roda na página.
      agendarNaPagina(id);
    }
  }
  return {
    resposta,
    cancelar() {
      const p = pendentes.get(id);
      if (!p) return;
      pendentes.delete(id);
      p.falha(new Error(CANCELADO));
    },
  };
}
